/**
 * Pins every Supabase edge-function call to the database's region. Without a
 * region, Supabase runs the function next to the caller, so a user in Europe
 * pays a round trip to the AU database for every query the function makes.
 * The `forceFunctionRegion` query parameter is the one supabase-js itself sets
 * for `invoke(..., { region })`; unlike the `x-region` header it adds nothing
 * to the CORS preflight. A call that already names a region keeps it.
 */

import { FunctionRegion } from '@supabase/supabase-js';

/** Every cell's database is in Sydney (`infra/cells/au-*.yaml`). */
export const DATABASE_FUNCTION_REGION = FunctionRegion.ApSoutheast2;

const REGION_PARAM = 'forceFunctionRegion';
const FUNCTIONS_PREFIX = '/functions/v1/';

function parseOrigin(value: string | undefined): string | null {
  try {
    return value ? new URL(value).origin : null;
  } catch {
    return null;
  }
}

const supabaseOrigin = parseOrigin(
  (import.meta.env as Record<string, string | undefined>).VITE_SUPABASE_URL,
);

/**
 * The `fetch` arguments with the region pinned on a Supabase edge-function
 * URL; any other request (PostgREST, auth, storage, another origin, a
 * `Request` object) gets its arguments back as they were.
 */
export function pinFunctionRegion(...args: Parameters<typeof fetch>): Parameters<typeof fetch> {
  const [input, ...rest] = args;
  if (supabaseOrigin === null || input instanceof Request) {
    return args;
  }
  let url: URL;
  try {
    url = new URL(input instanceof URL ? input.href : input);
  } catch {
    return args;
  }
  if (
    url.origin !== supabaseOrigin ||
    !url.pathname.startsWith(FUNCTIONS_PREFIX) ||
    url.searchParams.has(REGION_PARAM)
  ) {
    return args;
  }
  url.searchParams.set(REGION_PARAM, DATABASE_FUNCTION_REGION);
  return [url.href, ...rest];
}
